import React, { useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import { ExternalLink, MessageCircle, Sparkles } from "lucide-react";

const PARTNERS = [
  { domain: "SJDS.app", url: "https://sjds.app", label: "SJDS.app" },
  { domain: "magnificrock.com", url: "https://magnificrock.com", label: "MagnificRock.com" },
  { domain: "NicaBros.com" },
  { domain: "GregMiceli.com/Digital-Nomad", url: "https://gregmiceli.com/digital-nomad", label: "GregMiceli.com/Digital-Nomad" },
];

// WordPress mShots — no API key required, returns a website screenshot.
// Width is the only param that matters for thumbnails.
const thumbFor = (url) =>
  `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=720`;

function PartnerCard({ domain, url, label }) {
  const [loaded, setLoaded] = useState(false);
  const href = url || `https://www.${domain}`;
  const text = label || domain;
  // Auto-shrink the font for the longest domains so every one fits on a single
  // line at the narrowest mobile width (~155px card).
  const len = text.length;
  const fontPx = len >= 26 ? 9 : len >= 22 ? 10 : len >= 20 ? 11 : 12;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`partner-${domain}`}
      className="group block rounded-2xl overflow-hidden bg-white border border-[color:var(--ink-2)]/10 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
    >
      <div className="aspect-[16/10] w-full bg-[color:var(--sand-2)] relative overflow-hidden">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
            Loading preview…
          </div>
        )}
        <img
          src={thumbFor(href)}
          alt={`${text} preview`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={`w-full h-full object-cover object-top transition-opacity duration-500 group-hover:scale-[1.02] ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      </div>
      <div className="p-2 sm:p-2.5 flex flex-col items-stretch gap-1.5">
        <p
          className="font-semibold text-[color:var(--ink)] leading-tight text-center whitespace-nowrap"
          style={{ fontSize: `${fontPx}px`, letterSpacing: "-0.01em" }}
          title={href}
        >
          {text}
        </p>
        <span className="inline-flex items-center justify-center gap-1 pill px-2.5 py-1 text-[11px] font-bold bg-[color:var(--navy)] text-white group-hover:bg-[color:var(--coral)]">
          <ExternalLink className="w-3 h-3" />
          Visit
        </span>
      </div>
    </a>
  );
}

export default function LocalPartners() {
  const { t } = useI18n();
  const wa = "50586156364";
  const waLink = `https://wa.me/${wa}?text=${encodeURIComponent("Hi Greg — I'd love a website like the Popoyo partner sites!")}`;
  return (
    <section
      id="partners"
      data-testid="local-partners"
      className="clay p-6 md:p-7 scroll-mt-24"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--teal)" }}>
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="eyebrow">{t("partners_eyebrow")}</p>
          <h2 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
            {t("partners_title")}
          </h2>
          <p className="text-sm text-[color:var(--ink-2)] mt-1">{t("partners_subtitle")}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
        {PARTNERS.map((p) => (
          <PartnerCard key={p.domain} domain={p.domain} url={p.url} label={p.label} />
        ))}
      </div>

      {/* CTA — contact Greg for a website */}
      <div
        className="mt-6 rounded-2xl p-4 sm:p-5 flex items-center gap-4 flex-wrap"
        style={{
          background: "linear-gradient(135deg, #1B3B6F 0%, #264653 100%)",
          color: "#fff",
        }}
      >
        <div className="flex-1 min-w-[220px]">
          <p className="text-[11px] uppercase tracking-widest opacity-80">{t("partners_cta_eyebrow")}</p>
          <p className="text-base sm:text-lg font-semibold mt-0.5" style={{ fontFamily: "Outfit" }}>
            {t("partners_cta_title")}
          </p>
          <p className="text-xs opacity-80 mt-1">{t("partners_cta_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <a
            href="https://www.GregMiceli.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="partners-portfolio-cta"
            className="inline-flex items-center gap-2 pill px-4 py-2.5 text-sm font-bold hover:scale-[1.03] transition-transform"
            style={{ background: "#fff", color: "#1B3B6F", boxShadow: "0 8px 24px rgba(255,255,255,0.18)" }}
          >
            <ExternalLink className="w-4 h-4" />
            Portfolio
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="partners-whatsapp-cta"
            className="inline-flex items-center gap-2 pill px-5 py-2.5 text-sm font-bold hover:scale-[1.03] transition-transform"
            style={{ background: "#25D366", color: "#fff", boxShadow: "0 8px 24px rgba(37,211,102,0.35)" }}
          >
            <MessageCircle className="w-4 h-4" />
            {t("partners_cta_button")}
          </a>
        </div>
      </div>
    </section>
  );
}
